import React, { useState, useCallback } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';

import SearchableInput from 'components/common/SearchableInput';
import { adminShelfApi, shelfApi } from 'api';
import { toastApiError } from 'utils/apiErrors';

const BatchTransferModal = ({ show, onHide, batch, onSuccess }) => {
  const [selectedShelf, setSelectedShelf] = useState(null);
  const [shelfSearchValue, setShelfSearchValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setSelectedShelf(null);
    setShelfSearchValue('');
  }, []);

  const closeModal = useCallback(() => {
    resetForm();
    onHide();
  }, [resetForm, onHide]);

  const handleShelfSelect = useCallback(
    item => {
      if (item.id === batch?.shelf?.id) {
        toast.warning('The batch is already on this shelf');
        return;
      }
      setSelectedShelf(item);
      setShelfSearchValue(item.code);
    },
    [batch?.shelf?.id],
  );

  const handleShelfInputChange = useCallback(
    term => {
      setShelfSearchValue(term);
      if (selectedShelf && term !== selectedShelf.code) {
        setSelectedShelf(null);
      }
    },
    [selectedShelf],
  );

  const handleTransfer = async () => {
    if (!selectedShelf || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const response = await adminShelfApi.batchTransfer({
        product_batch_id: batch.id,
        destination_shelf_id: selectedShelf.id,
      });

      toast.success(
        response?.merged
          ? 'Batch transferred and merged into an existing batch on the destination shelf'
          : 'Batch transferred successfully',
      );
      closeModal();
      onSuccess();
    } catch (error) {
      toastApiError(error, 'Failed to transfer batch');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={isSubmitting ? undefined : closeModal}
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
    >
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>Transfer Batch</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Product</Form.Label>
          <Form.Control
            type="text"
            value={
              batch
                ? `${batch.product?.sku ?? '-'} — ${batch.product?.name ?? '-'}`
                : ''
            }
            disabled
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Batch</Form.Label>
          <Form.Control
            type="text"
            value={
              batch
                ? `${batch.batch_code || '-'} / Expiry: ${
                    batch.expire_date || '-'
                  } / Qty: ${batch.quantity ?? '-'}`
                : ''
            }
            disabled
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Current Shelf</Form.Label>
          <Form.Control type="text" value={batch?.shelf?.code || ''} disabled />
        </Form.Group>

        <Form.Group>
          <Form.Label>
            Destination Shelf<span className="text-danger">*</span>
          </Form.Label>
          <SearchableInput
            apiService={shelfApi}
            searchField="code__icontains"
            additionalParams={{ active: 'true' }}
            displayField="code"
            value={shelfSearchValue}
            onChange={handleShelfInputChange}
            onSelect={handleShelfSelect}
            placeholder="Enter 2 or more characters to search shelf..."
            renderItem={item => (
              <div className="font-medium">
                {item?.name} ({item?.code})
              </div>
            )}
            visibleItems={5}
          />
          <Form.Text className="text-600">
            If the destination shelf already has a batch with the same batch
            code and expiry date, the quantities will be merged.
          </Form.Text>
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={closeModal}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleTransfer}
          disabled={!selectedShelf || isSubmitting}
        >
          {isSubmitting ? 'Transferring...' : 'Transfer'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BatchTransferModal;
